import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PageShell from '../components/layout/PageShell';
import PlannerWeek from '../components/planner/PlannerWeek';
import SafeIcon from '../common/SafeIcon';
import { plannerPhases, plannerStorageKeys } from '../data/actionPlanner';

const getStoredArray = (key) => {
  try {
    const value = JSON.parse(window.localStorage.getItem(key));
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
};

export default function ActionPlanner() {
  const [completedTasks, setCompletedTasks] = useState(
    () => getStoredArray(plannerStorageKeys.completed)
  );
  const [priority, setPriority] = useState(
    () => window.localStorage.getItem(plannerStorageKeys.priority) || ''
  );

  const totalTasks = useMemo(
    () => plannerPhases.reduce((total, phase) => total + phase.tasks.length, 0),
    []
  );
  const progress = Math.round((completedTasks.length / totalTasks) * 100);

  useEffect(() => {
    window.localStorage.setItem(
      plannerStorageKeys.completed,
      JSON.stringify(completedTasks)
    );
  }, [completedTasks]);

  useEffect(() => {
    window.localStorage.setItem(plannerStorageKeys.priority, priority);
  }, [priority]);

  const toggleTask = (taskId) => {
    setCompletedTasks((current) => (
      current.includes(taskId)
        ? current.filter((id) => id !== taskId)
        : [...current, taskId]
    ));
  };

  const resetPlanner = () => {
    if (!window.confirm('Reset every completed action and clear your priority?')) return;
    setCompletedTasks([]);
    setPriority('');
  };

  return (
    <PageShell mainClassName="flex-grow overflow-hidden bg-[#F7F8F7]">
      <section className="px-5 pb-20 pt-28 sm:px-6 md:pt-36 lg:px-8 lg:pb-28">
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-10 lg:grid-cols-[1fr_22rem] lg:items-end">
            <header className="max-w-4xl">
              <p className="font-michroma text-xs font-bold uppercase tracking-[0.2em] text-[#409FA1]">
                Diagnose → Interpret → Deploy
              </p>
              <h1 className="mt-6 text-balance text-4xl font-bold leading-tight tracking-tight sm:text-5xl lg:text-7xl">
                30-Day Reputation Action Planner
              </h1>
              <p className="mt-6 max-w-3xl text-lg leading-8 text-[#244A49]/70">
                Turn your reputation signal into a practical month of work.
                Complete each action in order, keep human judgment in the
                process, and finish with one clear priority for the next cycle.
              </p>
            </header>

            <aside className="border border-[#244A49]/10 bg-white p-6 shadow-card">
              <div className="flex items-end justify-between gap-4">
                <div>
                  <p className="font-michroma text-[0.65rem] font-bold uppercase tracking-[0.16em] text-[#409FA1]">
                    Overall Progress
                  </p>
                  <p className="mt-3 text-4xl font-bold">{progress}%</p>
                </div>
                <p className="text-sm font-semibold text-[#244A49]/55">
                  {completedTasks.length} of {totalTasks}
                </p>
              </div>
              <div
                className="mt-5 h-2 overflow-hidden rounded-full bg-[#244A49]/10"
                role="progressbar"
                aria-label="Planner completion"
                aria-valuemin="0"
                aria-valuemax="100"
                aria-valuenow={progress}
              >
                <div
                  className="h-full rounded-full bg-[#2CB5B0] transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </aside>
          </div>

          <div className="mt-14 grid gap-5 lg:grid-cols-2">
            {plannerPhases.map((phase) => (
              <PlannerWeek
                key={phase.number}
                phase={phase}
                completedTasks={completedTasks}
                onToggle={toggleTask}
              />
            ))}
          </div>

          <section className="mt-5 grid overflow-hidden border border-[#244A49]/10 bg-[#244A49] text-white lg:grid-cols-[1fr_auto]">
            <div className="p-7 sm:p-10">
              <p className="font-michroma text-xs font-bold uppercase tracking-[0.18em] text-[#2CB5B0]">
                Next 30-Day Priority
              </p>
              <label htmlFor="next-priority" className="mt-4 block text-2xl font-bold">
                What deserves attention next?
              </label>
              <textarea
                id="next-priority"
                value={priority}
                onChange={(event) => setPriority(event.target.value)}
                rows="3"
                maxLength="300"
                placeholder="Example: Build a consistent review request into every completed service visit."
                className="mt-5 w-full resize-none rounded-xl border border-white/15 bg-white/10 px-4 py-3 leading-7 text-white outline-none placeholder:text-white/35 focus:border-[#2CB5B0]"
              />
              <p className="mt-3 text-xs leading-5 text-white/50">
                Progress and notes are stored only in this browser. Nothing is
                submitted to RepUpgrade.
              </p>
            </div>

            <div className="flex flex-col justify-center gap-3 border-t border-white/10 p-7 lg:min-w-64 lg:border-l lg:border-t-0">
              <button
                type="button"
                onClick={() => window.print()}
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-white px-5 font-bold text-[#244A49] transition hover:bg-[#F7F8F7]"
              >
                <SafeIcon name="Printer" className="h-5 w-5" />
                Print Planner
              </button>
              <button
                type="button"
                onClick={resetPlanner}
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl border border-white/20 px-5 font-bold text-white transition hover:bg-white/10"
              >
                <SafeIcon name="RefreshCw" className="h-5 w-5" />
                Reset Progress
              </button>
            </div>
          </section>

          <section className="mt-16 rounded-[2rem] bg-white px-7 py-12 text-center shadow-card sm:px-10">
            <p className="font-michroma text-xs font-bold uppercase tracking-[0.18em] text-[#409FA1]">
              Start With Evidence
            </p>
            <h2 className="mx-auto mt-4 max-w-2xl text-3xl font-bold tracking-tight">
              Need a reputation baseline before you begin?
            </h2>
            <p className="mx-auto mt-5 max-w-2xl leading-7 text-[#244A49]/65">
              Start with the externally operated reputation report, then use
              this planner to organize the next practical actions.
            </p>
            <Link
              to="/create-access"
              className="cta-glow mt-8 inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-[#F49558] px-7 py-4 font-bold text-white shadow-cta transition hover:bg-[#D35E0E]"
            >
              Get My Reputation Report
              <SafeIcon name="ArrowRight" className="h-5 w-5" />
            </Link>
          </section>
        </div>
      </section>
    </PageShell>
  );
}